import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { each } from 'lodash-es';
import {
  emailLengthValidator,
  emailPatternWithPlus,
} from 'src/app/auth/helpers';
import { SubscriptionLevel } from 'src/app/auth/models/user';
import { BrazeService } from 'src/app/services/braze.service';
import { BillingLevel, BILLINGLEVELOPTIONS } from 'src/app/utils/billingLevel';

import { STATES } from 'src/app/utils/states';
import { AccountNameValidator } from 'src/app/_helpers/account-name-validator';
import {
  checkCCExpireValidator,
  checkCreditCard,
  checkZipCode,
} from 'src/app/_helpers/custom-validators';
import { UserNameValidator } from 'src/app/_helpers/existing-user-name-validator';
import { getCreditCardType } from 'src/app/_helpers/get-credit-card-type';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import {
  FormField,
  JsonFormValidators,
  Validations,
} from 'src/app/_models/form-field';
import { CurrencyState } from 'src/app/_models/state/optionUIState';
import { environment } from 'src/environments/environment';
@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnChanges, OnDestroy {
  @Input() jsonFormData: FormField[];
  @Input() displayCurrency: CurrencyState;
  @Input() parentForm: FormGroup;
  @Input() controlName: string;
  dynamicForm: FormGroup;
  @Output() isdynamicFormValid: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() isEnterKeyPress: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() controls: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  featureCost = BILLINGLEVELOPTIONS.find((F) => F.name === BillingLevel.PREMIUM)
    .cost;
  maxUser = environment.maxNoOfUser;
  private sm = new SubscriptionManager();
  constructor(
    private accountNameValidator: AccountNameValidator,
    private userNameValidator: UserNameValidator,
    private brazeService: BrazeService
  ) {}

  setFormGroup() {
    const controls = {};
    each(this.jsonFormData, (res: FormField) => {
      const validations: Validations = this.setValidations(res.validations);
      controls[res.fieldName] = new FormControl(
        res.value,
        validations?.staticValidations,
        validations?.asyncValidations
      );
    });

    this.dynamicForm = new FormGroup(controls);
    this.parentForm.removeControl(this.controlName);
    this.parentForm.addControl(this.controlName, this.dynamicForm);
    this.setCountry();
    this.changeState();
    this.getTotalAmount();
    this.setCardType();
    this.checkUseAccountAddress();
    this.setCcExpiry();
    if (this.dynamicFormControls.expirationYear) {
      this.dynamicForm.setValidators(
        checkCCExpireValidator('expirationYear', 'expirationMonth')
      );
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      this[propName] = changes[propName]?.currentValue;
    }

    this.setFormGroup();
    this.sm.manage = (
      this.parentForm.get(this.controlName) as FormGroup
    ).statusChanges.subscribe((value) => {
      if (value === 'VALID') {
        this.isdynamicFormValid.emit(true);
      } else if (value === 'PENDING') {
        // support async validators
        setTimeout(() => {
          this.parentForm.updateValueAndValidity({
            emitEvent: true,
          }); // this function will trigger statusChange one more time.
        }, 2000);
      } else {
        this.isdynamicFormValid.emit(false);
      }
    });
  }
  setValidations(validations: JsonFormValidators): Validations {
    const staticValidationsArray = [];
    const asyncValidationsArray = [];
    if (validations?.staticValidations) {
      for (const [key, value] of Object.entries(
        validations.staticValidations
      )) {
        switch (key) {
          case 'required':
            staticValidationsArray.push(Validators.required);
            break;

          case 'emailPattern':
            staticValidationsArray.push(
              Validators.pattern(emailPatternWithPlus),
              emailLengthValidator()
            );
            break;
          case 'max':
            staticValidationsArray.push(Validators.max(value));
            break;

          case 'min':
            staticValidationsArray.push(Validators.min(value));
            break;

          case 'phoneNumberValidator':
            staticValidationsArray.push(
              Validators.maxLength(20),
              Validators.pattern(
                /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im
              )
            );
            break;
          case 'creditCardValidator':
            staticValidationsArray.push(
              Validators.required,
              Validators.pattern('^[0-9]*$'),
              Validators.minLength(15),
              Validators.maxLength(19),
              checkCreditCard()
            );
            break;
          case 'checkCvv':
            staticValidationsArray.push(
              Validators.required,
              Validators.pattern('^[0-9]{3,4}$')
            );
        }
      }
      // adding async validators
      if (validations?.asyncValidations) {
        for (const [key, value] of Object.entries(
          validations.asyncValidations
        )) {
          if (key === 'checkAccountDuplicateName') {
            asyncValidationsArray.push(this.accountNameValidator.validate);
          }
          if (key === 'UserNameValidator') {
            asyncValidationsArray.push(this.userNameValidator.validate);
          }
        }
      }
    }
    return {
      staticValidations: staticValidationsArray,
      asyncValidations: asyncValidationsArray,
    };
  }
  get dynamicFormControls(): { [key: string]: AbstractControl } {
    return (this.parentForm.get(this.controlName) as FormGroup).controls;
  }
  setCountry() {
    if (this.dynamicFormControls.stateProvince) {
      this.sm.manage =
        this.dynamicFormControls.stateProvince.valueChanges.subscribe(
          (val: string) => {
            if (val) {
              STATES.forEach((state) => {
                if (state.abbreviation.includes(val)) {
                  this.dynamicFormControls.country.setValue(state.country, {
                    emitEvent: false,
                  });
                }
                this.dynamicForm.setValidators(
                  checkZipCode('country', 'zipCode')
                );
              });
            } else {
              this.dynamicFormControls.country.setValue('', {
                emitEvent: false,
              });
              this.dynamicFormControls.country.markAsDirty();
            }
          }
        );
      this.sm.manage = this.dynamicFormControls.zipCode.valueChanges.subscribe(
        () => {
          this.dynamicForm.setValidators(checkZipCode('country', 'zipCode'));
        }
      );
    }
  }

  changeState() {
    if (this.dynamicFormControls.stateProvince) {
      this.sm.manage = this.dynamicFormControls.country.valueChanges.subscribe(
        (ctry) => {
          this.dynamicFormControls.country.setValue(ctry, {
            emitEvent: false,
          });
          this.dynamicFormControls.stateProvince.setValue('', {
            emitEvent: false,
          });
          this.dynamicFormControls.stateProvince.markAsDirty();
          this.dynamicForm.setValidators(checkZipCode('country', 'zipCode'));
        }
      );
    }
  }
  getTotalAmount() {
    if (this.dynamicFormControls.noOfUsers) {
      this.sm.manage =
        this.dynamicFormControls.noOfUsers.valueChanges.subscribe((val) => {
          if (val) {
            if (val > this.maxUser) {
              this.brazeService.track(
                `User entered value(${val}) exceeds the limit in number of users in Sign up page`
              );
            }
            this.dynamicFormControls.totalCostPerMonth.setValue(
              val > 0 && val <= this.maxUser
                ? (val * this.featureCost).toFixed(2)
                : 0
            );
          }
        });
    }
  }
  checkKeyPress(event) {
    if (event.target.id === 'noOfTrucks' || event.target.id === 'noOfUsers') {
      return event.which >= 48;
    }
    if (event.target.id === 'emailAddress' && event.key === 'Enter') {
      this.isEnterKeyPress.emit(true);
    }
  }

  checkUseAccountAddress() {
    if (this.dynamicFormControls.useAccountAddress) {
      this.dynamicFormControls.useAccountAddress.valueChanges.subscribe(
        (value: boolean) => {
          if (value) {
            this.controls.emit(
              this.parentForm.get(this.controlName) as FormGroup
            );
          } else {
            this.controls.emit(null);
          }
        }
      );
    }
  }
  setCcExpiry() {
    if (this.dynamicFormControls.expirationMonth) {
      this.sm.manage =
        this.dynamicFormControls.expirationMonth.valueChanges.subscribe(() => {
          this.dynamicForm.setValidators(
            checkCCExpireValidator('expirationYear', 'expirationMonth')
          );
        });
      this.sm.manage =
        this.dynamicFormControls.expirationYear.valueChanges.subscribe(() => {
          this.dynamicForm.setValidators(
            checkCCExpireValidator('expirationYear', 'expirationMonth')
          );
        });
    }
  }

  setCardType() {
    if (this.dynamicFormControls.cardNumber) {
      this.sm.manage =
        this.dynamicFormControls.cardNumber.statusChanges.subscribe((val) => {
          if (val === 'VALID') {
            const cardType = getCreditCardType(
              this.dynamicFormControls.cardNumber.value
            );
            if (cardType) {
              this.dynamicFormControls.cardType.setValue(cardType, {
                emitEvent: false,
              });
            }
          }
        });
    }
  }
  ngOnDestroy(): void {
    this.sm.unsubscribe();
  }
}
